import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction, SearchField } from '../../components/data-grid/RouteDataGrid';
import { EntityDetailDialog, auditTrailFields } from '../../components/dialogs/EntityDetailDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import { fmtDateTime } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { Issue } from '../../api/types';
import {
  useApprovePlan, useIssuesList, useMyPermissions,
} from '../../api/queries';
import {
  IssueStatusChip, categoryFromLabels, priorityFromLabels, statusFromLabels,
} from './StatusChip';
import { IssueFormDialog } from './IssueFormDialog';

// RouteDataGrid's TRow generic requires `{ id: string }`. GitHub issues
// use `number: int` as the primary key, so we wrap rows to add a
// stringified `id` for grid bookkeeping. The number stays canonical
// everywhere else.
type IssueRow = Issue & { id: string };

/**
 * Issues page. PORTAL_FORM_RULES-conforming: RouteDataGrid + EntityDetail
 * + audit-trail block + CSV/PDF export. State filter is server-side
 * (state=all so closed issues are visible too); quickFilters slice the
 * result client-side by status / category label.
 */
export default function IssuesPage() {
  usePageTitle('Issues');
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );

  // Fetch open + closed in one go so the "Completed" tab works without
  // re-querying. GitHub caps per_page at 100; the API forwards that.
  const raw = useIssuesList('all');
  const queryKey = raw.queryKey;
  const listFn = useCallback(
    async (): Promise<IssueRow[]> =>
      (await raw.listFn()).map(i => ({ ...i, id: String(i.number) })),
    [raw],
  );

  const [detailRow, setDetailRow] = useState<Issue | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<Issue | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  const columns = useMemo<GridColDef<IssueRow>[]>(() => [
    { field: 'number', headerName: '#', width: 80,
      type: 'number', align: 'right', headerAlign: 'right',
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            #{p.row.number}
          </Typography>
        </Box>
      ) },
    { field: 'title', headerName: 'Title', flex: 1.6,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" noWrap title={p.row.title}>
            {p.row.title}
          </Typography>
        </Box>
      ) },
    { field: 'status', headerName: 'Status', width: 180, sortable: false,
      valueGetter: (_v, row) => statusFromLabels(row.labels) ?? '',
      renderCell: (p) => {
        const s = statusFromLabels(p.row.labels);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {s ? <IssueStatusChip label={s} /> : <span>—</span>}
          </Box>
        );
      } },
    { field: 'category', headerName: 'Category', width: 120, sortable: false,
      valueGetter: (_v, row) => categoryFromLabels(row.labels) ?? '',
      renderCell: (p) => {
        const c = categoryFromLabels(p.row.labels);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {c ? <Chip size="small" label={c} variant="outlined" /> : <span>—</span>}
          </Box>
        );
      } },
    { field: 'priority', headerName: 'Priority', width: 130, sortable: false,
      valueGetter: (_v, row) => priorityFromLabels(row.labels) ?? '',
      renderCell: (p) => {
        const pr = priorityFromLabels(p.row.labels);
        if (!pr) return <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>—</Box>;
        const colour: 'error' | 'warning' | 'info' | 'default' =
          pr.startsWith('p1') ? 'error'   :
          pr.startsWith('p2') ? 'warning' :
          pr.startsWith('p3') ? 'info'    : 'default';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip size="small" label={pr} color={colour} variant="outlined" />
          </Box>
        );
      } },
    { field: 'state', headerName: 'State', width: 100,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip size="small"
                label={p.row.state}
                color={p.row.state === 'open' ? 'primary' : 'default'}
                variant={p.row.state === 'open' ? 'filled' : 'outlined'} />
        </Box>
      ) },
    { field: 'createdAt', headerName: 'Created', width: 180,
      valueFormatter: (v) => fmtDateTime(v) },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text', key: 'title', label: 'Title contains' },
  ], []);

  // Quick filter set — first ALL OPEN, then status pipeline stages, then
  // completed (= closed). Each stage filters by the status:* label, so
  // even closed items with status:completed land in the right tab.
  const quickFilters = useMemo<QuickFilter<IssueRow>[]>(() => [
    { label: 'All open',   filter: (r) => r.state === 'open' },
    { label: 'Raised',     filter: (r) => statusFromLabels(r.labels) === 'raised' },
    { label: 'Verified',   filter: (r) => statusFromLabels(r.labels) === 'verified' },
    { label: 'Approved',   filter: (r) => statusFromLabels(r.labels) === 'approved-for-development' },
    { label: 'Planned',    filter: (r) => statusFromLabels(r.labels) === 'planned' },
    { label: 'Ready',      filter: (r) => statusFromLabels(r.labels) === 'ready-for-development' },
    { label: 'In review',  filter: (r) => statusFromLabels(r.labels) === 'in-review' },
    { label: 'Completed',  filter: (r) => r.state === 'closed' || statusFromLabels(r.labels) === 'completed' },
    { label: 'All',        filter: () => true },
  ], []);

  const exportColumns = useMemo<ExportColumn<IssueRow>[]>(() => [
    { key: 'number',      label: '#' },
    { key: 'title',       label: 'Title' },
    { key: 'state',       label: 'State' },
    { key: 'labels',      label: 'Status',
      format: (v) => statusFromLabels(v as string[]) ?? '' },
    { key: 'labels',      label: 'Category',
      format: (v) => categoryFromLabels(v as string[]) ?? '' },
    { key: 'labels',      label: 'Priority',
      format: (v) => priorityFromLabels(v as string[]) ?? '' },
    { key: 'htmlUrl',     label: 'GitHub URL' },
    { key: 'createdAt',   label: 'Created', format: (v) => fmtDateTime(v) },
    { key: 'updatedAt',   label: 'Updated', format: (v) => fmtDateTime(v) },
  ], []);

  // Detail dialog is pure READ-ONLY (PORTAL_FORM_RULES §5). All state
  // transitions happen via row actions or the Edit form — no interactive
  // controls inside the View dialog.
  const detailFields = useMemo<DetailRow<Issue>[]>(() => [
    { label: '#',            render: (r) => <code>#{r.number}</code> },
    { label: 'Title',        render: (r) => r.title },
    { label: 'Status',       render: (r) => {
      const s = statusFromLabels(r.labels);
      return s ? <IssueStatusChip label={s} /> : <span>—</span>;
    } },
    { label: 'Category',     render: (r) => categoryFromLabels(r.labels) ?? '—' },
    { label: 'Priority',     render: (r) => priorityFromLabels(r.labels) ?? '—' },
    { label: 'GitHub state', render: (r) => r.state },
    { label: 'Link',         render: (r) => (
      <Button size="small" variant="outlined"
              href={r.htmlUrl} target="_blank" rel="noopener noreferrer"
              endIcon={<OpenInNewIcon fontSize="small" />}>
        Open on GitHub
      </Button>
    ) },
    { separator: true, fullWidth: true, label: 'Description',
      render: (r) => (
        <Typography component="pre"
          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                fontFamily: 'inherit', fontSize: 14, m: 0 }}>
          {r.body || '—'}
        </Typography>
      ) },
    { fullWidth: true, label: 'Comments',
      render: (r) => r.comments.length === 0
        ? <Typography variant="body2" color="text.secondary">—</Typography>
        : (
          <Stack spacing={1.5}>
            {r.comments.map(c => (
              <Box key={c.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {c.authorLogin}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtDateTime(c.createdAt)}
                  </Typography>
                </Stack>
                <Typography component="pre"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        fontFamily: 'inherit', fontSize: 13, m: 0 }}>
                  {c.body}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) },
    ...auditTrailFields<Issue>(),
  ], []);

  // Row actions — house pattern from Sites: View / Edit (gated) /
  // workflow actions (Approve plan, conditional on status + plan
  // comment) opening a ConfirmDialog.
  const approvePlan = useApprovePlan();
  const [approveTarget, setApproveTarget] = useState<IssueRow | null>(null);
  const isApprovable = useCallback((row: IssueRow) =>
    statusFromLabels(row.labels) === 'status:planned'
    && row.comments.some(c => c.body.includes('Claude Development Plan')),
  []);

  const actions = useMemo<RowAction<IssueRow>[]>(() => [
    { label: 'View',
      onClick: (row) => setDetailRow(row) },
    { label: 'Edit',
      visible: () => has('issues.edit'),
      onClick: (row) => setEditRow(row) },
    { label: 'Approve plan',
      visible: (row) => has('issues.edit') && isApprovable(row),
      onClick: (row) => setApproveTarget(row) },
  ], [has, isApprovable]);

  return (
    <Stack spacing={2}>
      <RouteDataGrid<IssueRow>
        entityName="Issues"
        queryKey={queryKey}
        listFn={listFn}
        columns={columns}
        searchFields={searchFields}
        quickFilters={quickFilters}
        exportColumns={exportColumns}
        reportTitle="ARMS — Issues"
        actions={actions}
        primaryAction={
          has('issues.edit')
            ? { label: '+ Raise issue', onClick: () => setCreateOpen(true) }
            : undefined
        }
        onRowClick={(row) => setDetailRow(row)}
        emptyMessage="No issues yet. Raise the first one to seed the pipeline."
      />

      <EntityDetailDialog<Issue>
        open={detailRow !== null}
        title={detailRow ? `#${detailRow.number} · ${detailRow.title}` : 'Issue'}
        data={detailRow ? withReporterFromBody(detailRow) : null}
        fields={detailFields}
        maxWidth="md"
        onClose={() => setDetailRow(null)}
      />

      <IssueFormDialog
        mode="create"
        open={createOpen}
        onClose={() => { setCreateOpen(false); setSnack('Issue created'); }}
      />

      {editRow && (
        <IssueFormDialog
          mode="edit"
          open={editRow !== null}
          issue={editRow}
          onClose={() => { setEditRow(null); setSnack('Issue updated'); }}
        />
      )}

      <ConfirmDialog
        open={approveTarget !== null}
        title="Approve plan"
        message={approveTarget ? (
          <>
            Approve the development plan on issue <strong>#{approveTarget.number}</strong>?
            Claude Code will pick this issue up on its next poll and open a PR.
          </>
        ) : ''}
        confirmLabel="Approve plan"
        busy={approvePlan.isPending}
        error={(approvePlan.error as { message?: string } | null)?.message}
        onConfirm={() => {
          if (!approveTarget) return;
          approvePlan.mutate(approveTarget.number, {
            onSuccess: () => {
              setApproveTarget(null);
              setSnack('Plan approved — Claude Code will start the implementation agent on its next poll');
            },
          });
        }}
        onCancel={() => setApproveTarget(null)}
      />

      <Snackbar
        open={snack !== null}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </Stack>
  );
}

/**
 * The issue body holds the human-readable "Reported By:" row that the
 * portal generated at create time; surface that into the detail dialog's
 * audit-trail block so PORTAL_FORM_RULES §9 ("Created By" present)
 * stays satisfied even though the GitHub PAT is the literal author of
 * record on the GH side.
 */
function withReporterFromBody(issue: Issue): Issue & {
  createdByName?: string | null;
  updatedByName?: string | null;
} {
  const reportedBy = parseReportedBy(issue.body);
  const lastComment = issue.comments[issue.comments.length - 1];
  return {
    ...issue,
    createdByName: reportedBy ?? 'via portal',
    updatedByName: lastComment?.authorLogin ?? reportedBy ?? null,
  };
}

function parseReportedBy(body: string): string | null {
  // Matches "| **Reported By** | <name> |" rendered by GitHubIssueService.
  const m = body.match(/\|\s*\*\*Reported By\*\*\s*\|\s*([^|]+?)\s*\|/);
  return m ? m[1].trim() : null;
}
